import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Toaster, useToaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SocketProvider, useSocket } from './context/SocketContext';
import { CurrencyProvider } from './context/CurrencyContext';
import API, { useRateLimitNotice } from './utils/api';
import { verifyEmailOnce, nextVerifyStatus } from './utils/verifyEmailOnce';

// Layout
import Nav    from './components/layout/Nav';
import Footer from './components/layout/Footer';
import InviteSplash, { hasInviteUnlock } from './components/InviteSplash';
import { InviteGateContext, useInviteGate } from './context/InviteGateContext';
import PublicLandingPage, { PublicPageShell } from './pages/PublicLandingPage';
import { isGuestOrderPath } from './utils/guestOrderToken';

// Auth pages — small, needed on every entry point
import { HomePage, LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';

// All other pages are lazy-loaded — each becomes its own JS chunk
const BrowsePage              = React.lazy(() => import('./pages/BrowsePage'));
const ListingTypeRouter       = React.lazy(() => import('./pages/ListingTypeRouter'));
const ListItemPage            = React.lazy(() => import('./pages/ListItemPage'));
const EditListingPage         = React.lazy(() => import('./pages/EditListingPage'));
const CheckoutPage            = React.lazy(() => import('./pages/CheckoutPage'));
const DashboardPage           = React.lazy(() => import('./pages/DashboardPage'));
const OrderDetailPage         = React.lazy(() => import('./pages/OrderDetailPage'));
const TrackOrderPage          = React.lazy(() => import('./pages/TrackOrderPage'));
const RentalStatusPage        = React.lazy(() => import('./pages/RentalStatusPage'));
const BarterBrowsePage        = React.lazy(() => import('./pages/BarterBrowsePage'));
const BarterDetailPage        = React.lazy(() => import('./pages/BarterDetailPage'));
const CreateBarterListingPage = React.lazy(() => import('./pages/CreateBarterListingPage'));
const FreeBrowsePage          = React.lazy(() => import('./pages/FreeBrowsePage'));
const FreeDetailPage          = React.lazy(() => import('./pages/FreeDetailPage'));
const CreateFreeListingPage   = React.lazy(() => import('./pages/CreateFreeListingPage'));
const UserProfilePage         = React.lazy(() => import('./pages/UserProfilePage'));
const WatchlistPage           = React.lazy(() => import('./pages/WatchlistPage'));
const ISOPage                 = React.lazy(() => import('./pages/ISOPage'));
const NotFoundPage            = React.lazy(() => import('./pages/NotFoundPage'));
const LiveBrowsePage          = React.lazy(() => import('./pages/LiveBrowsePage'));
const LivePage                = React.lazy(() => import('./pages/LivePage'));
const LiveWatchPage           = React.lazy(() => import('./pages/LiveWatchPage'));
const QuickListPage           = React.lazy(() => import('./pages/QuickListPage'));
// Named exports — webpack deduplicates: all OtherPages vars share one chunk
const MessagesPage       = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.MessagesPage })));
const SettingsPage       = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.SettingsPage })));
const AdminPage          = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.AdminPage })));
const OrdersPage         = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.OrdersPage })));
const BookingsPage       = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.BookingsPage })));
const RatingsPage        = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.RatingsPage })));
const ResponseTimePage   = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.ResponseTimePage })));
const SalesPage          = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.SalesPage })));
const ReviewsPage        = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.ReviewsPage })));
const MyReviewsPage      = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.MyReviewsPage })));
const CategoriesPage        = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.CategoriesPage })));
const CategoryLandingPage   = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.CategoryLandingPage })));
const SharedWishlistPage    = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.SharedWishlistPage })));
const SharedCollectionPage  = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.SharedCollectionPage })));
// All LegalPages components share one chunk
const TermsPage           = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage         = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const CookiesPage         = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.CookiesPage })));
const RefundPage          = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.RefundPage })));
const AcceptableUsePage   = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.AcceptableUsePage })));
const CommunityPage       = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.CommunityPage })));
const DmcaPage            = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.DmcaPage })));
const OwnerAgreementPage  = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.OwnerAgreementPage })));
const TrustSafetyPage     = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TrustSafetyPage })));
const HowItWorksPage      = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.HowItWorksPage })));
const FeesPage            = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.FeesPage })));
const HelpPage            = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.HelpPage })));
const SellerGuidePage     = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.SellerGuidePage })));
const AboutPage           = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.AboutPage })));
const SitemapPage         = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.SitemapPage })));
const BuyerProtectionPage = React.lazy(() => import('./pages/LegalPages').then(m => ({ default: m.BuyerProtectionPage })));

// CSS
import './styles/global.css';

// ── Error Boundary ────────────────────────────────
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App error:', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🍒</div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 28, lineHeight: 1.6 }}>
            An unexpected error occurred. Please refresh the page or go back home.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-cherry" onClick={() => window.location.href = '/'}>Go home</button>
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>Refresh</button>
          </div>
          <pre style={{ marginTop: 24, padding: 16, background: 'var(--white)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 11, textAlign: 'left', overflow: 'auto', color: 'var(--cherry)', maxHeight: 200 }}>
            {this.state.error?.toString()}
          </pre>
        </div>
      </div>
    );
  }
}

// ── Scroll to top on every route change ──────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ── Protected route ───────────────────────────────
function Protected({ children, adminOnly = false, allowGuestToken = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 48 }} className="float-anim">🍒</div>
      <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading...</div>
      <RateLimitHint />
    </div>
  );

  // Guest-checkout order links (#gt=... or the legacy ?guestToken=..., or the token kept for this tab
  // once the page removed it from the URL) open without an account; the backend checks the token.
  const guestLink = allowGuestToken && isGuestOrderPath(location);
  if (!user && !guestLink) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  if (adminOnly && !['admin', 'superadmin', 'moderator'].includes(user.role)) return <Navigate to="/" replace />;

  return children;
}


// ── Soft-launch invite gate ───────────────────────────
// While the gate is on and the visitor is neither logged in nor invite-unlocked, the
// marketplace (every route rendered by AppRoutes) stays behind InviteSplash. Only these
// public, read-only pages bypass it, rendered in a minimal public shell (no marketplace
// Nav, cart, sockets or API calls):
//   /                          → PublicLandingPage (unless ?invite=CODE is present)
//   /terms, /privacy           → TermsPage, PrivacyPage
//   /refunds (and /refund)     → RefundPage
// Everything else (including /invite/:code and ?invite=CODE) shows InviteSplash as before.
// Prod hotfix: account pages are never gated, so existing users without a session (or
// with a cleared invite flag) can always log in or reset a password.
const AUTH_ALWAYS_OPEN = new Set(['/login', '/forgot-password', '/reset-password', '/verify-email']);

const PUBLIC_LEGAL_PAGES = {
  '/terms':   TermsPage,
  '/privacy': PrivacyPage,
  '/refunds': RefundPage,
  '/refund':  RefundPage,
};

function normalizePublicPath(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '').toLowerCase();
  return p || '/';
}

// QA r12 O1: while the API answers 429, loading screens say so (with the wait) instead of only
// "Loading…".
function RateLimitHint() {
  const msg = useRateLimitNotice();
  if (!msg) return null;
  return <div role="status" style={{ fontSize: 13, color: 'var(--cherry)', fontWeight: 600 }}>{msg}</div>;
}

function PublicPageFallback() {
  return (
    <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 48 }} className="float-anim">🍒</div>
      <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading…</div>
      <RateLimitHint />
    </div>
  );
}

// /orders/<id>#gt=<token> (or legacy ?guestToken=<token>, or the token kept for this tab), no other
// path: the link sent to guest buyers. QA r3: see utils/guestOrderToken.js.
function isGuestOrderLink(location) {
  return isGuestOrderPath(location);
}

function InviteGateProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [gateEnabled, setGateEnabled] = useState(null); // null = loading
  const [unlocked, setUnlocked] = useState(() => hasInviteUnlock());
  const [showSplash, setShowSplash] = useState(false); // landing → "Enter invite code"

  useEffect(() => {
    let cancelled = false;
    API.get('/invites/gate-status')
      .then((r) => { if (!cancelled) setGateEnabled(!!r.data?.enabled); })
      // r7: fail closed. If the flag can't be read, keep the soft-launch gate on (login,
      // legal pages and an unlocked browser still work); the server enforces it on signup anyway.
      .catch(() => { if (!cancelled) setGateEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  // Still resolving auth or gate flag — avoid splash flash for logged-in users
  if (authLoading || gateEnabled === null) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: 'var(--bg)' }}>
        <div style={{ fontSize: 48 }} className="float-anim">🍒</div>
        <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading…</div>
        <RateLimitHint />
      </div>
    );
  }

  if (!gateEnabled || unlocked || user) {
    return (
      <InviteGateContext.Provider value={{ gateEnabled, locked: false, enterInvite: null }}>
        {children}
      </InviteGateContext.Provider>
    );
  }

  // ── Gate is on and the visitor has not passed it ──
  const path = normalizePublicPath(location.pathname);
  const hasInviteParam = new URLSearchParams(location.search).has('invite');

  const enterInvite = () => {
    setShowSplash(true);
    if (path !== '/') navigate('/');
  };

  // UX review: the pages below render the marketplace Layout for a gated visitor; `locked` lets the
  // chrome hide marketplace links that would only lead back to InviteSplash.
  const lockedChildren = (
    <InviteGateContext.Provider value={{ gateEnabled: true, locked: true, enterInvite }}>
      {children}
    </InviteGateContext.Provider>
  );

  // QA r5: an emailed guest order link (/orders/:id#gt=… or legacy ?guestToken=…) opens on a fresh device without the
  // invite code. Only that page is let through; the token itself is checked by the API.
  if (isGuestOrderLink(location)) {
    return lockedChildren;
  }

  if (AUTH_ALWAYS_OPEN.has(path)) {
    return lockedChildren;
  }

  const PublicLegalPage = PUBLIC_LEGAL_PAGES[path];
  if (PublicLegalPage) {
    return (
      <PublicPageShell onEnterInvite={enterInvite}>
        <Suspense fallback={<PublicPageFallback />}>
          <PublicLegalPage />
        </Suspense>
      </PublicPageShell>
    );
  }

  if (path === '/' && !hasInviteParam && !showSplash) {
    return <PublicLandingPage onEnterInvite={enterInvite} />;
  }

  return (
    <InviteSplash
      onUnlocked={() => setUnlocked(true)}
      onBack={() => { setShowSplash(false); if (path !== '/') navigate('/'); }}
    />
  );
}

// ── Site status (maintenance + announcement) ──────────────
function SiteStatusProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    API.get('/site-status').then(r => setStatus(r.data)).catch(() => {});
    const iv = setInterval(() => API.get('/site-status').then(r => setStatus(r.data)).catch(() => {}), 60000);
    return () => clearInterval(iv);
  }, []);

  const isAdmin = user && ['admin','superadmin'].includes(user.role);

  if (status?.maintenanceMode && !isAdmin) {
    return (
      <div style={{ minHeight:'100dvh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'var(--bg)', gap:16, padding:24, textAlign:'center' }}>
        <div style={{ fontSize:64 }}>🔧</div>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700 }}>Down for Maintenance</h1>
        <p style={{ color:'var(--text-soft)', maxWidth:400 }}>{status.maintenanceMessage}</p>
      </div>
    );
  }

  const bannerColors = { info:'#EBF0FF', success:'#EAF5EC', warning:'#FFF9E6', error:'#FDECEA' };
  const bannerText   = { info:'#1A5276', success:'#2D7A3A', warning:'#C9930A', error:'var(--cherry)' };

  return (
    <>
      {status?.announcement && (
        <div style={{ background: bannerColors[status.announcementType]||bannerColors.info, color: bannerText[status.announcementType]||bannerText.info, textAlign:'center', padding:'10px 16px', fontSize:14, fontWeight:600 }}>
          {status.announcement}
        </div>
      )}
      {children}
    </>
  );
}

// ── Layout wrapper ────────────────────────────────
function AdminBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  if (!user || !['admin', 'superadmin'].includes(user.role)) return null;
  if (location.pathname === '/admin') return null;
  return (
    <div style={{ background:'#1A1A2E', color:'white', fontSize:12, padding:'6px 20px', display:'flex', alignItems:'center', gap:12 }}>
      <span style={{ opacity:.6 }}>🛡️ Admin</span>
      <button onClick={() => navigate('/admin')}
        style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', fontSize:12, fontWeight:600, padding:'8px 14px', borderRadius:100, cursor:'pointer', minHeight:40 }}>
        ← Back to Admin Panel
      </button>
      <span style={{ opacity:.4, marginLeft:4 }}>{location.pathname}</span>
    </div>
  );
}

function Layout({ children }) {
  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Nav />
      <AdminBar />
      <main id="main-content" className="page-wrapper">{children}</main>
      <Footer />
    </>
  );
}

// ── No-footer pages (messages has its own layout) ───────────────
function ChatLayout({ children }) {
  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Nav />
      <main id="main-content">{children}</main>
    </>
  );
}

// ── Email verification page ───────────────────────────
function EmailVerifyPage() {
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const [status, setStatus] = useState('verifying');
  const [msg, setMsg]       = useState('');
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  // The token is single-use: verify once per token (verifyEmailOnce shares one request across
  // effect re-runs and remounts), and once success is shown never let a failure replace it.
  const statusRef = useRef('verifying');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); setMsg('No verification token found in this link.'); return; }
    let active = true; // ignore a response that arrives after this effect was cleaned up
    verifyEmailOnce((url, body) => API.post(url, body), token).then((result) => {
      if (!active) return;
      const next = nextVerifyStatus(statusRef.current, result);
      if (next === statusRef.current) return;
      statusRef.current = next;
      setStatus(next);
      if (next === 'success') { if (updateUser) updateUser({ emailVerified: true }); }
      else setMsg(result.error);
    });
    return () => { active = false; };
  }, []); // eslint-disable-line

  if (status === 'verifying') return (
    <div style={{ textAlign:'center', padding:'100px 40px' }}>
      <div style={{ fontSize:48, marginBottom:16 }} className="float-anim">🍒</div>
      <p style={{ color:'var(--text-soft)' }}>Verifying your email…</p>
    </div>
  );

  if (status === 'success') return (
    <div style={{ textAlign:'center', padding:'100px 40px', maxWidth:480, margin:'0 auto' }}>
      <div style={{ fontSize:56, marginBottom:16 }}>✅</div>
      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:12 }}>Email Verified!</h1>
      <p style={{ color:'var(--text-soft)', marginBottom:28 }}>Your email address has been confirmed. You're all set.</p>
      {user
        ? <button className="btn btn-cherry" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
        : <button className="btn btn-cherry" onClick={() => navigate('/login')}>Log in</button>
      }
    </div>
  );

  return (
    <div style={{ textAlign:'center', padding:'100px 40px', maxWidth:480, margin:'0 auto' }}>
      <div style={{ fontSize:56, marginBottom:16 }}>❌</div>
      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:12 }}>Verification Failed</h1>
      <p style={{ color:'var(--text-soft)', marginBottom:28 }}>{msg}</p>
      <button className="btn btn-ghost" onClick={() => navigate('/')}>Go home</button>
    </div>
  );
}

// ── Simple placeholder ────────────────────────────
function Placeholder({ title, icon = '🍒', desc = 'This page is coming soon.' }) {
  return (
    <div style={{ textAlign: 'center', padding: '100px 40px', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }} className="float-anim">{icon}</div>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 30, fontWeight: 700, marginBottom: 12 }}>{title}</h1>
      <p style={{ fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.7, marginBottom: 24 }}>{desc}</p>
      <div style={{ display: 'inline-flex', gap: 8, padding: '10px 20px', background: 'var(--cherry-light)', borderRadius: 100, fontSize: 13, fontWeight: 600, color: 'var(--cherry)' }}>
        🚀 Coming soon
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────
function AppRoutes() {
  return (
    <Suspense fallback={
      <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
        <div style={{ fontSize:48 }} className="float-anim">🍒</div>
        <div style={{ fontSize:14, color:'var(--text-soft)' }}>Loading…</div>
        <RateLimitHint />
      </div>
    }>
    <Routes>
      {/* Public */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/browse" element={<Layout><BrowsePage /></Layout>} />
      <Route path="/listing/:id" element={<Layout><ListingTypeRouter /></Layout>} />
      <Route path="/profile/:id" element={<Layout><UserProfilePage /></Layout>} />
      <Route path="/barter" element={<Layout><BarterBrowsePage /></Layout>} />
      <Route path="/barter/:id" element={<Layout><BarterDetailPage /></Layout>} />
      <Route path="/free" element={<Layout><FreeBrowsePage /></Layout>} />
      <Route path="/free/:id" element={<Layout><FreeDetailPage /></Layout>} />
      <Route path="/looking-for" element={<Layout><ISOPage /></Layout>} />
      <Route path="/live" element={<Layout><LiveBrowsePage /></Layout>} />
      <Route path="/live/go" element={<Protected><LivePage /></Protected>} />
      <Route path="/live/:roomName" element={<LiveWatchPage />} />
      <Route path="/wishlist/:userId" element={<Layout><SharedWishlistPage /></Layout>} />
      <Route path="/collection/:collectionId" element={<Layout><SharedCollectionPage /></Layout>} />
      <Route path="/how-it-works" element={<Layout><HowItWorksPage /></Layout>} />

      {/* Auth */}
      <Route path="/invite/:code"  element={<Layout><HomePage /></Layout>} />
      <Route path="/login"            element={<Layout><LoginPage /></Layout>} />
      <Route path="/signup"           element={<Layout><SignupPage /></Layout>} />
      <Route path="/forgot-password"  element={<Layout><ForgotPasswordPage /></Layout>} />
      <Route path="/reset-password"   element={<Layout><ResetPasswordPage /></Layout>} />
      <Route path="/verify-email"     element={<Layout><EmailVerifyPage /></Layout>} />

      {/* Protected */}
      <Route path="/list" element={<Protected><Layout><ListItemPage /></Layout></Protected>} />
      <Route path="/quick-list" element={<Protected><QuickListPage /></Protected>} />
      <Route path="/listing/:id/edit" element={<Protected><Layout><EditListingPage /></Layout></Protected>} />
      <Route path="/barter/create" element={<Protected><Layout><CreateBarterListingPage /></Layout></Protected>} />
      <Route path="/free/create" element={<Protected><Layout><CreateFreeListingPage /></Layout></Protected>} />
      <Route path="/checkout" element={<Layout><CheckoutPage /></Layout>} />
      <Route path="/track" element={<Layout><TrackOrderPage /></Layout>} />
      <Route path="/orders/:id" element={<Protected allowGuestToken><Layout><OrderDetailPage /></Layout></Protected>} />
      {/* QA r8: /rentals had no page (404). Send it to the rentals browse view the nav uses. */}
      <Route path="/rentals" element={<Navigate to="/browse?type=rent" replace />} />
      <Route path="/rentals/:id" element={<Protected><Layout><RentalStatusPage /></Layout></Protected>} />

      {/* Dashboard routes */}
      <Route path="/dashboard"                  element={<Protected><Layout><DashboardPage /></Layout></Protected>} />
      <Route path="/dashboard/:tab"             element={<Protected><Layout><DashboardPage /></Layout></Protected>} />
      <Route path="/dashboard/watchlist"        element={<Protected><Layout><WatchlistPage /></Layout></Protected>} />
      <Route path="/dashboard/orders"           element={<Protected><Layout><OrdersPage /></Layout></Protected>} />
      <Route path="/dashboard/bookings"         element={<Protected><Layout><BookingsPage /></Layout></Protected>} />
      {/* QA r8: rental dispute notifications link to /dashboard/rentals (no such tab) */}
      <Route path="/dashboard/rentals"          element={<Navigate to="/dashboard/bookings" replace />} />
      <Route path="/dashboard/ratings"          element={<Protected><Layout><RatingsPage /></Layout></Protected>} />
      <Route path="/dashboard/response-time"    element={<Protected><Layout><ResponseTimePage /></Layout></Protected>} />
      <Route path="/dashboard/sales"            element={<Protected><Layout><SalesPage /></Layout></Protected>} />
      <Route path="/dashboard/reviews"          element={<Protected><Layout><ReviewsPage /></Layout></Protected>} />
      <Route path="/dashboard/my-reviews"       element={<Protected><Layout><MyReviewsPage /></Layout></Protected>} />

      {/* Messages */}
      <Route path="/messages"              element={<Protected><ChatLayout><MessagesPage /></ChatLayout></Protected>} />
      <Route path="/messages/:conversationId" element={<Protected><ChatLayout><MessagesPage /></ChatLayout></Protected>} />

      {/* Settings */}
      <Route path="/settings" element={<Protected><Layout><SettingsPage /></Layout></Protected>} />

      {/* Admin */}
      <Route path="/admin" element={<Protected adminOnly><Layout><AdminPage /></Layout></Protected>} />

      {/* Info pages */}
      <Route path="/categories"         element={<Layout><CategoriesPage /></Layout>} />
      <Route path="/categories/:slug"   element={<Layout><CategoryLandingPage /></Layout>} />
      <Route path="/fees"          element={<Layout><FeesPage /></Layout>} />
      <Route path="/help"          element={<Layout><HelpPage /></Layout>} />
      <Route path="/seller-guide"  element={<Layout><SellerGuidePage /></Layout>} />
      <Route path="/about"         element={<Layout><AboutPage /></Layout>} />
      <Route path="/sitemap"       element={<Layout><SitemapPage /></Layout>} />
      {/* Legal pages */}
      <Route path="/terms"            element={<Layout><TermsPage /></Layout>} />
      <Route path="/privacy"          element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/cookies"          element={<Layout><CookiesPage /></Layout>} />
      <Route path="/refund"           element={<Layout><RefundPage /></Layout>} />
      <Route path="/refunds"          element={<Layout><RefundPage /></Layout>} />
      <Route path="/acceptable-use"   element={<Layout><AcceptableUsePage /></Layout>} />
      <Route path="/community"            element={<Layout><CommunityPage /></Layout>} />
      <Route path="/community-guidelines" element={<Layout><CommunityPage /></Layout>} />
      <Route path="/dmca"             element={<Layout><DmcaPage /></Layout>} />
      <Route path="/owner-agreement"  element={<Layout><OwnerAgreementPage /></Layout>} />
      <Route path="/trust"            element={<Layout><TrustSafetyPage /></Layout>} />
      <Route path="/buyer-protection" element={<Layout><BuyerProtectionPage /></Layout>} />

      {/* Friendly redirects for common alternate URLs */}
      <Route path="/sell" element={<Navigate to="/list" replace />} />
      <Route path="/cart" element={<Navigate to="/checkout" replace />} />
      <Route path="/watchlist" element={<Navigate to="/dashboard/watchlist" replace />} />

      {/* 404 */}
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
    </Suspense>
  );
}

// Global "/" keyboard shortcut — focus browse search from anywhere
function GlobalSearchShortcut() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      if (location.pathname === '/browse') {
        window.dispatchEvent(new CustomEvent('cpkd:focus-search'));
      } else {
        navigate('/browse?focus=search');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [location.pathname, navigate]); // eslint-disable-line

  return null;
}

// Show toast when a new message arrives via socket (not on messages page)
function GlobalMessageToast() {
  const { user } = useAuth();
  const { onNewMessage } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    return onNewMessage(msg => {
      if (msg.sender_id === user.id) return;
      if (location.pathname.startsWith('/messages')) return;
      const name = msg.sender_name || 'Someone';
      toast(`💬 New message from ${name}`, {
        duration: 5000,
        onClick: () => navigate(msg.conversation_id ? `/messages/${msg.conversation_id}` : '/messages'),
      });
    });
  }, [user, location.pathname, onNewMessage, navigate]); // eslint-disable-line

  return null;
}

// Check localStorage auction reminders every minute; fire a toast when within 1h of end
// Mobile bottom navigation bar — hidden on desktop via CSS
function MobileBottomNav() {
  const { user } = useAuth();
  const { locked } = useInviteGate();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const pollRef = useRef(null);
  const [dark, setDark] = useState(() => document.documentElement.getAttribute('data-theme') === 'dark');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!user) { setUnreadMsgs(0); return; }
    const fetch = () => API.get('/messages/unread-count').then(r => setUnreadMsgs(r.data.count || 0)).catch(() => {});
    fetch();
    pollRef.current = setInterval(fetch, 60000);
    return () => clearInterval(pollRef.current);
  }, [user]); // eslint-disable-line

  useEffect(() => { if (pathname === '/messages') setUnreadMsgs(0); }, [pathname]);

  const notifPollRef = useRef(null);
  useEffect(() => {
    if (!user) { setUnreadNotifs(0); return; }
    const fetch = () => API.get('/notifications/unread-count').then(r => setUnreadNotifs(r.data.count || 0)).catch(() => {});
    fetch();
    notifPollRef.current = setInterval(fetch, 60000);
    return () => clearInterval(notifPollRef.current);
  }, [user]); // eslint-disable-line
  useEffect(() => { if (pathname.startsWith('/dashboard')) setUnreadNotifs(0); }, [pathname]);

  const [visible, setVisible] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setVisible(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []); // eslint-disable-line
  // UX review: while the invite gate locks the visitor out, every tab except "Log in" leads to
  // InviteSplash, so the bar is hidden (body.bottom-nav-hidden drops its reserved padding).
  useEffect(() => {
    document.body.classList.toggle('bottom-nav-hidden', locked);
    return () => document.body.classList.remove('bottom-nav-hidden');
  }, [locked]);
  if (!visible || locked) return null;

  // Whole path segments only: '/list' (Sell) must not light up on '/listing/…' pages.
  const isActive = (path) => path === '/' ? pathname === '/' : (pathname === path || pathname.startsWith(path + '/'));

  const items = [
    { icon: '🏠', label: 'Home',     path: '/' },
    { icon: '🔍', label: 'Browse',   path: '/browse' },
    { icon: '➕', label: 'Sell',     path: '/list' },
    { icon: '💬', label: 'Messages', path: '/messages', badge: unreadMsgs },
    { icon: '👤', label: user ? 'Account' : 'Log in', path: user ? '/dashboard' : '/login', badge: user ? unreadNotifs : 0 },
  ];

  return (
    <nav style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--white)', borderTop:'1px solid var(--border)', display:'flex', zIndex:8000, paddingBottom:'env(safe-area-inset-bottom, 0px)' }}>
      {items.map(item => {
        const active = isActive(item.path);
        return (
          <button key={item.path} onClick={() => navigate(item.path)}
            style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'8px 0 6px', background:'none', border:'none', cursor:'pointer', gap:1, position:'relative' }}>
            <span style={{ fontSize:20, position:'relative', display:'inline-block', lineHeight:1 }}>
              {item.icon}
              {item.badge > 0 && (
                <span style={{ position:'absolute', top:-4, right:-6, minWidth:14, height:14, borderRadius:7, background:'var(--cherry)', color:'white', fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </span>
            <span style={{ fontSize:9, fontWeight:700, letterSpacing:.3, color: active ? 'var(--cherry)' : 'var(--text-soft)', marginTop:2 }}>{item.label}</span>
            {active && <span style={{ position:'absolute', bottom:0, left:'50%', transform:'translateX(-50%)', width:20, height:2, background:'var(--cherry)', borderRadius:2 }} />}
          </button>
        );
      })}
      <button
        onClick={() => {
          const next = !dark;
          document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
          document.body.style.background = next ? '#161412' : '';
          document.body.style.color = next ? '#EDE8E3' : '';
          localStorage.setItem('cherrypkd_theme', next ? 'dark' : 'light');
        }}
        style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'8px 0 6px', background:'none', border:'none', cursor:'pointer', gap:1, color:'var(--text-soft)' }}>
        <span style={{ fontSize:20, lineHeight:1 }}>{dark ? '☀️' : '🌙'}</span>
        <span style={{ fontSize:9, fontWeight:700, letterSpacing:.3, marginTop:2 }}>{dark ? 'Light' : 'Dark'}</span>
      </button>
    </nav>
  );
}

function LiveToastAnnouncer() {
  const { toasts } = useToaster();
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    const visible = toasts.filter(t => t.visible);
    if (!visible.length) return;
    const latest = visible[visible.length - 1];
    const msg = typeof latest.message === 'string' ? latest.message : '';
    setAnnounced('');
    requestAnimationFrame(() => setAnnounced(msg));
  }, [toasts]);
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {announced}
    </div>
  );
}

function AuctionReminderChecker() {
  const navigate = useNavigate();
  useEffect(() => {
    const check = () => {
      try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('cherrypkd_remind_'));
        keys.forEach(key => {
          const item = JSON.parse(localStorage.getItem(key) || 'null');
          if (!item) return;
          const minsLeft = (new Date(item.endsAt) - Date.now()) / 60000;
          if (minsLeft <= 60 && minsLeft > 0) {
            localStorage.removeItem(key);
            import('react-hot-toast').then(({ default: t }) => {
              t(`⏰ "${item.title}" ends in ~${Math.ceil(minsLeft)} min!`, {
                duration: 8000,
                onClick: () => navigate(`/listing/${item.id}`),
              });
            });
          } else if (minsLeft <= 0) {
            localStorage.removeItem(key);
          }
        });
      } catch {}
    };
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, [navigate]);
  return null;
}

// QA r3: floating buttons are hidden where they cover primary actions (checkout / cart / order
// flows). On phones the dark toggle is CSS-hidden because MobileBottomNav has its own.
const FAB_HIDDEN_PREFIXES = ['/checkout', '/cart', '/orders', '/order-success', '/payment'];
const fabHiddenOn = (pathname) => FAB_HIDDEN_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));

function DarkModeToggle() {
  const { pathname } = useLocation();
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('cherrypkd_theme');
    if (saved) return saved === 'dark';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    document.body.style.background = dark ? '#161412' : '';
    document.body.style.color = dark ? '#EDE8E3' : '';
    localStorage.setItem('cherrypkd_theme', dark ? 'dark' : 'light');
  }, [dark]);
  // Stay in sync when the bottom-nav toggle flips the theme.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setDark(d => (d === isDark ? d : isDark));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  if (fabHiddenOn(pathname)) return null;
  return (
    <button
      onClick={() => setDark(d => !d)}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="dark-toggle-btn"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{ background: dark ? '#3A3530' : '#F5F0EB', border:'1px solid var(--border)' }}>
      {dark ? '☀️' : '🌙'}
    </button>
  );
}

// UX review: the Add-to-Home-Screen banner waits until the visitor has engaged (logged in, or
// PWA_MIN_PAGE_VIEWS non-auth page views this session) and never shows on auth pages or while the
// invite gate locks the visitor out.
const PWA_AUTH_PREFIXES = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/invite'];
const PWA_MIN_PAGE_VIEWS = 2;
const PWA_VIEWS_KEY = 'cherrypkd_pwa_views';
const isPwaAuthPath = (pathname) => PWA_AUTH_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));
// UX r6: the fixed banner sat on top of the chat composer, so it is not shown on the messages routes.
const isPwaMessagesPath = (pathname) => pathname === '/messages' || pathname.startsWith('/messages/');

function PWAInstallPrompt() {
  const { user } = useAuth();
  const { locked } = useInviteGate();
  const { pathname } = useLocation();
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('pwa_dismissed') === '1');
  const [views, setViews] = useState(() => {
    try { return parseInt(sessionStorage.getItem(PWA_VIEWS_KEY) || '0', 10) || 0; } catch { return 0; }
  });

  useEffect(() => {
    const handler = e => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (isPwaAuthPath(pathname)) return;
    setViews(v => {
      const next = v + 1;
      try { sessionStorage.setItem(PWA_VIEWS_KEY, String(next)); } catch { /* private mode */ }
      return next;
    });
  }, [pathname]);

  const engaged = !!user || views >= PWA_MIN_PAGE_VIEWS;
  if (!prompt || dismissed || !engaged || locked || isPwaAuthPath(pathname) || isPwaMessagesPath(pathname)) return null;

  const dismiss = () => { setDismissed(true); localStorage.setItem('pwa_dismissed', '1'); };

  return (
    <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background:'#1A1A1A', color:'white', borderRadius:16, padding:'14px 20px', display:'flex', alignItems:'center', gap:14, boxShadow:'0 8px 32px rgba(0,0,0,.3)', zIndex:9000, maxWidth:380, width:'calc(100vw - 48px)' }}>
      <span style={{ fontSize:28, flexShrink:0 }}>🍒</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>Add CherryPKD to Home Screen</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,.55)' }}>Shop and sell faster with the app</div>
      </div>
      <button
        onClick={async () => {
          prompt.prompt();
          const { outcome } = await prompt.userChoice;
          setPrompt(null);
          if (outcome === 'dismissed') dismiss();
        }}
        style={{ background:'#C0392B', border:'none', color:'white', borderRadius:8, padding:'8px 16px', fontWeight:700, fontSize:13, cursor:'pointer', flexShrink:0 }}>
        Install
      </button>
      <button onClick={dismiss} aria-label="Dismiss" style={{ background:'none', border:'none', color:'rgba(255,255,255,.4)', fontSize:20, cursor:'pointer', padding:'10px', lineHeight:1, flexShrink:0, minWidth:44, minHeight:44, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
    </div>
  );
}

// QA r3: the camera FAB only shows on the browse grids (not home, checkout, dashboard, forms or
// list rows it used to cover). Position/size live in global.css (.camera-quick-btn) so the
// mobile-only media query is not overridden by an inline display, and it sits above the bottom
// nav with a safe-area offset. It is hidden while the hamburger menu is open (body.nav-menu-open).
const CAMERA_FAB_PATHS = ['/browse', '/barter', '/free', '/live'];

function CameraQuickList() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();
  const show = CAMERA_FAB_PATHS.includes(pathname) && !fabHiddenOn(pathname);
  // UX r2: the button steps aside once the footer scrolls into view, so it never covers footer links.
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('has-camera-fab', show);
    return () => document.body.classList.remove('has-camera-fab');
  }, [show]);

  useEffect(() => {
    setFooterInView(false);
    if (!show || typeof IntersectionObserver === 'undefined') return undefined;
    let io = null;
    const t = setTimeout(() => {
      const footer = document.querySelector('.site-footer');
      if (!footer) return;
      io = new IntersectionObserver(([entry]) => setFooterInView(entry.isIntersecting));
      io.observe(footer);
    }, 300);
    return () => { clearTimeout(t); if (io) io.disconnect(); };
  }, [show, pathname]);

  if (!show || footerInView) return null;
  return (
    <button
      onClick={() => isLoggedIn ? navigate('/quick-list') : navigate('/login')}
      title="Quick-list with camera"
      aria-label="Quick-list with camera"
      className="camera-quick-btn">
      📷
    </button>
  );
}

// UX r2: a small banner whenever the build uses a Stripe test key (pk_test_…), so nobody mistakes
// staging for the live store. Builds with a live key (pk_live_…) never show it.
const STRIPE_TEST_MODE = String(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test');

function StripeTestModeBanner() {
  if (!STRIPE_TEST_MODE) return null;
  return (
    <div role="note" data-testid="stripe-test-mode-banner"
      style={{ background:'#FFF4D6', color:'#5C4400', borderBottom:'1px solid #E8C96A', fontSize:12, fontWeight:700, textAlign:'center', padding:'4px 12px', letterSpacing:.3 }}>
      TEST MODE · Payments use Stripe test cards. No real charges are made.
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <CurrencyProvider>
        <SiteStatusProvider>
        <InviteGateProvider>
        <CartProvider>
          <SocketProvider>
          <StripeTestModeBanner />
          <AppRoutes />
          <MobileBottomNav />
          <GlobalSearchShortcut />
          <GlobalMessageToast />
          <LiveToastAnnouncer />
          <CameraQuickList />
          <DarkModeToggle />
          <AuctionReminderChecker />
          <PWAInstallPrompt />
          <Toaster
            position="bottom-center"
            containerClassName="app-toaster"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#1A1A1A',
                color: '#fff',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 100,
                padding: '12px 20px',
              },
            }}
          />
          </SocketProvider>
        </CartProvider>
        </InviteGateProvider>
        </SiteStatusProvider>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
